export const MOBILE_SQUARE_BANNER_SIZE = 1080

const DEFAULT_OUTPUT_TYPE = 'image/jpeg'

function resolveOutputType(fileType = '') {
  if (fileType === 'image/png') return 'image/png'
  if (fileType === 'image/webp') return 'image/webp'
  if (fileType === 'image/jpeg' || fileType === 'image/jpg') return 'image/jpeg'

  return DEFAULT_OUTPUT_TYPE
}

function resolveOutputExtension(outputType = DEFAULT_OUTPUT_TYPE) {
  if (outputType === 'image/png') return 'png'
  if (outputType === 'image/webp') return 'webp'

  return 'jpg'
}

function buildOutputName(name = '', outputType = DEFAULT_OUTPUT_TYPE) {
  const baseName = String(name || 'banner').replace(/\.[^.]+$/, '')
  const extension = resolveOutputExtension(outputType)

  return baseName + '-' + MOBILE_SQUARE_BANNER_SIZE + 'x' + MOBILE_SQUARE_BANNER_SIZE + '.' + extension
}

function loadImageFromFile(file) {
  return new Promise((resolve, reject) => {
    const image = new Image()
    const objectUrl = URL.createObjectURL(file)

    image.onload = () => {
      URL.revokeObjectURL(objectUrl)
      resolve({
        image,
        width: image.naturalWidth || image.width,
        height: image.naturalHeight || image.height,
      })
    }

    image.onerror = () => {
      URL.revokeObjectURL(objectUrl)
      reject(new Error('No pudimos leer la imagen.'))
    }

    image.src = objectUrl
  })
}

function canvasToFile(canvas, sourceFile, outputType) {
  return new Promise((resolve, reject) => {
    const quality = outputType === 'image/png' ? undefined : 0.92

    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error('No pudimos preparar la portada para mobile.'))
        return
      }

      resolve(new File([blob], buildOutputName(sourceFile && sourceFile.name, outputType), {
        type: outputType,
        lastModified: Date.now(),
      }))
    }, outputType, quality)
  })
}

function drawBlurredBackground(ctx, image, width, height) {
  const coverScale = Math.max(MOBILE_SQUARE_BANNER_SIZE / width, MOBILE_SQUARE_BANNER_SIZE / height)
  const coverWidth = width * coverScale
  const coverHeight = height * coverScale
  const bleed = 64

  ctx.save()
  ctx.filter = 'blur(34px) saturate(0.92) brightness(0.7)'
  ctx.drawImage(
    image,
    (MOBILE_SQUARE_BANNER_SIZE - coverWidth) / 2 - bleed / 2,
    (MOBILE_SQUARE_BANNER_SIZE - coverHeight) / 2 - bleed / 2,
    coverWidth + bleed,
    coverHeight + bleed
  )
  ctx.restore()

  const overlay = ctx.createLinearGradient(0, 0, 0, MOBILE_SQUARE_BANNER_SIZE)
  overlay.addColorStop(0, 'rgba(7, 10, 16, 0.08)')
  overlay.addColorStop(1, 'rgba(7, 10, 16, 0.22)')

  ctx.save()
  ctx.fillStyle = overlay
  ctx.fillRect(0, 0, MOBILE_SQUARE_BANNER_SIZE, MOBILE_SQUARE_BANNER_SIZE)
  ctx.restore()
}

function drawContainedImage(ctx, image, width, height) {
  const containScale = Math.min(MOBILE_SQUARE_BANNER_SIZE / width, MOBILE_SQUARE_BANNER_SIZE / height)
  const drawWidth = Math.round(width * containScale)
  const drawHeight = Math.round(height * containScale)

  ctx.drawImage(
    image,
    Math.round((MOBILE_SQUARE_BANNER_SIZE - drawWidth) / 2),
    Math.round((MOBILE_SQUARE_BANNER_SIZE - drawHeight) / 2),
    drawWidth,
    drawHeight
  )
}

export function isExactMobileSquareBanner(width, height) {
  return Number(width) === MOBILE_SQUARE_BANNER_SIZE && Number(height) === MOBILE_SQUARE_BANNER_SIZE
}

export function isSquareBanner(width, height) {
  const numericWidth = Number(width) || 0
  const numericHeight = Number(height) || 0

  return Boolean(numericWidth && numericHeight && numericWidth === numericHeight)
}

export function classifyBannerShape(width, height) {
  const numericWidth = Number(width) || 0
  const numericHeight = Number(height) || 0

  if (!numericWidth || !numericHeight) {
    return 'unknown'
  }

  if (isSquareBanner(numericWidth, numericHeight)) {
    return 'square'
  }

  return numericWidth > numericHeight ? 'landscape' : 'portrait'
}

export async function normalizeBannerForMobile(file) {
  if (!file) return file

  const { image, width, height } = await loadImageFromFile(file)

  if (!width || !height || isSquareBanner(width, height)) {
    return file
  }

  const canvas = document.createElement('canvas')
  canvas.width = MOBILE_SQUARE_BANNER_SIZE
  canvas.height = MOBILE_SQUARE_BANNER_SIZE

  const ctx = canvas.getContext('2d')
  if (!ctx) {
    throw new Error('No pudimos preparar la portada para mobile.')
  }

  drawBlurredBackground(ctx, image, width, height)
  drawContainedImage(ctx, image, width, height)

  return canvasToFile(canvas, file, resolveOutputType(file.type))
}
